import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';

import { DeclarationService } from '../../core/entity/declaration.service';
import { Declaration } from '../../core/entity/declaration.types';
import { DashPipe } from '../pipes/dash.pipe';
import { IconComponent } from '../icon/icon';
import { ConfirmDialogComponent, ConfirmDialogData } from '../confirm-dialog/confirm-dialog';
import { AccountDialogComponent } from '../declaration-dialog/account-dialog';
import {
  DeclarationDialogComponent,
  DeclarationDialogData,
  DeclarationDialogResult,
} from '../declaration-dialog/declaration-dialog';
import { DeclarationListKind, LIST_SPECS } from './declaration-specs';

export type { DeclarationListKind } from './declaration-specs';

/**
 * The affirmation, rendered as a STATE and never as a live boolean.
 *
 * 🚨 `UI-DA-58-04` (DOC-58 §68): where a hold's state is an Ops record rather
 * than a system determination it renders as *recorded by a named actor, as at a
 * recorded moment* — **never as "now"**. FLOW-277's hold is released on the
 * strength of this affirmation, so every state below carries who and when, and
 * the retracted state carries BOTH acts: the confirmation that was made, and
 * the later act that retracted it. A screen showing only "confirmed ✓" would be
 * asserting a current system state over a record that is nothing of the kind.
 *
 * `NONE` carries no actor on purpose — there is no record to attribute, and
 * inventing one would be the same lie in the other direction.
 */
export type AffirmationState =
  | { state: 'IN_FORCE'; by: string; at: string }
  | { state: 'RETRACTED'; by: string; at: string; retractedBy: string; retractedAt: string }
  | { state: 'NONE' };

/**
 * The declared lists — funders, related parties, directors (FLOW-243).
 *
 * **One panel for three lists, deliberately.** They are the same act with
 * different columns: the business telling us a fact about itself, attributably
 * and at a moment in time. Three components would be three places to fix the
 * withdrawal semantics.
 *
 * 🚨 **There is no delete, and the UI must not imply one.** A correction
 * appends a WITHDRAWN row naming the row it retracts; the original stays
 * readable and stays attributed to whoever made it. So the action is labelled
 * "Withdraw", the withdrawn rows stay on screen behind a toggle, and nothing
 * in the copy says "remove".
 *
 * ⚠️ **`mode`** picks the door, not the styling. `client` writes the business's
 * own declaration; `ops` records what CapX found, which is a different act and
 * is stored as one. `readOnly` renders the Ops read without the add form.
 */
@Component({
  selector: 'app-declarations-panel',
  imports: [MatButtonModule, MatCardModule, DatePipe, DashPipe, IconComponent],
  templateUrl: './declarations-panel.html',
  styleUrl: './declarations-panel.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeclarationsPanelComponent implements OnInit {
  private readonly declarations = inject(DeclarationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);

  readonly entityShortId = input.required<string>();
  /** Which door to write through — see the class note. */
  readonly mode = input<'client' | 'ops'>('client');
  /** Ops surfaces that only read: the list renders, the add form does not. */
  readonly readOnly = input(false);
  /** Which lists to render, in order. */
  readonly lists = input<DeclarationListKind[]>(['FUNDER', 'RELATED_PARTY', 'DIRECTOR']);
  /** Whether the accounts section renders; by default, in Ops mode only. */
  readonly accounts = input<boolean | null>(null);
  /** Narrow the director list to these roles — a context-stack card shows one side. */
  readonly roles = input<string[] | null>(null);
  /**
   * Inside a context-stack card (Ops): no card chrome and no section title — the
   * card's own name is the title. The acts are unchanged.
   */
  readonly embedded = input(false);
  /** After a write lands and the list re-reads, so a host can refresh its own summary. */
  readonly changed = output<void>();

  protected readonly rows = signal<Declaration[]>([]);
  protected readonly isLoading = signal(true);
  protected readonly loadError = signal(false);
  protected readonly isSaving = signal(false);
  protected readonly serverError = signal<string | null>(null);
  /** Withdrawn rows are hidden by default but never discarded (append-only). */
  protected readonly showWithdrawn = signal(false);
  protected readonly withdrawingId = signal<string | null>(null);

  protected readonly specs = computed(() => this.lists().map((k) => LIST_SPECS[k]));

  // --- Declared accounts (FLOW-280) -----------------------------------------
  //
  // ⚠️ **Ops-mode only, and that is not a styling decision.** The client's
  // accounts surface is the wizard's bank step, where declaring an account,
  // attributing statements and affirming the list are one guided sequence. A
  // second client-side accounts list here would be a second place to declare
  // the same thing. This section exists because Ops release FLOW-277's hold on
  // the strength of these rows and could not previously see any of them.
  protected readonly showsAccounts = computed(() => this.accounts() ?? this.mode() === 'ops');

  /** Accounts on the list now — `is_in_force`, never `status` (see `liveOf`). */
  protected readonly liveAccounts = computed(() =>
    this.rows().filter((r) => r.kind === 'ACCOUNT' && r.is_in_force),
  );

  /** Accounts taken off the list. Behind the same toggle as the other kinds. */
  protected readonly withdrawnAccounts = computed(() =>
    this.rows().filter((r) => r.kind === 'ACCOUNT' && !r.is_in_force && r.status === 'DECLARED'),
  );

  /**
   * The affirmation as a three-way state — see {@link AffirmationState}.
   *
   * Derived from the rows rather than read off a field, because there is no
   * field: the register is append-only, so "retracted" is the EXISTENCE of a
   * later WITHDRAWN row naming this one, and the retracted row itself still
   * carries `status: 'DECLARED'`. Reading a status here would report every
   * affirmation ever made as still standing.
   */
  protected readonly affirmation = computed<AffirmationState>(() => {
    const rows = this.rows().filter((r) => r.kind === 'ACCOUNT_AFFIRMATION');
    const live = rows.find((r) => r.is_in_force);
    if (live) {
      return { state: 'IN_FORCE', by: live.declared_by_name, at: live.declared_at };
    }
    // The most recent retraction. Several affirm→change→re-affirm cycles are
    // legitimate, and it is the latest act that describes the state now.
    const retraction = rows
      .filter((r) => r.status === 'WITHDRAWN')
      .sort((a, b) => a.declared_at.localeCompare(b.declared_at))
      .at(-1);
    if (!retraction) return { state: 'NONE' };
    const retracted = rows.find((r) => r.short_id === retraction.withdraws);
    return {
      state: 'RETRACTED',
      by: retracted?.declared_by_name ?? retraction.declared_by_name,
      at: retracted?.declared_at ?? retraction.declared_at,
      retractedBy: retraction.declared_by_name,
      retractedAt: retraction.declared_at,
    };
  });

  /**
   * Narrow accessors for the template — the derivation lives in
   * {@link affirmation} and these only pick a branch out of it, so there is one
   * place the state is decided and the markup cannot reach a fourth answer.
   */
  protected readonly affirmationInForce = computed(() => {
    const a = this.affirmation();
    return a.state === 'IN_FORCE' ? a : null;
  });

  protected readonly affirmationRetracted = computed(() => {
    const a = this.affirmation();
    return a.state === 'RETRACTED' ? a : null;
  });

  /**
   * Four states, and the fourth is the blank the client never answered. 🚨
   * Never a yes/no: an unticked box records *"no trading goes through this
   * account"*, which is a claim nobody made.
   */
  protected readonly tradingStates = [
    { value: 'YES', label: 'Yes' },
    { value: 'NO', label: 'No' },
    { value: 'NOT_SURE', label: 'Not sure' },
  ];

  protected readonly facilityTypes = [
    { value: 'DEBTOR_FINANCE', label: 'Debtors Finance' },
    { value: 'TERM_LOAN', label: 'Term loan' },
    { value: 'OVERDRAFT', label: 'Overdraft' },
    { value: 'ASSET_FINANCE', label: 'Asset finance' },
    { value: 'OTHER', label: 'Other' },
  ];

  /**
   * Four states, and "Not sure" is one of them — never a yes/no. A client who
   * does not know whether a facility is settled must be able to say so; forcing
   * the answer manufactures a fact nobody stated.
   */
  protected readonly facilityStates = [
    { value: 'LIVE', label: 'Still running' },
    { value: 'SETTLED', label: 'Settled' },
    { value: 'NOT_SURE', label: 'Not sure' },
  ];

  protected readonly relationships = [
    { value: 'HOLDING', label: 'Holding company' },
    { value: 'SUBSIDIARY', label: 'Subsidiary' },
    { value: 'COMMON_DIRECTOR', label: 'Shared director' },
    { value: 'FAMILY', label: 'Family' },
    { value: 'OTHER', label: 'Other' },
  ];

  protected readonly directorRoles = [
    { value: 'DIRECTOR', label: 'Director' },
    { value: 'SHAREHOLDER', label: 'Shareholder' },
    { value: 'BOTH', label: 'Director and shareholder' },
  ];

  ngOnInit(): void {
    this.load();
  }

  protected load(): void {
    this.isLoading.set(true);
    this.loadError.set(false);
    const read$ =
      this.mode() === 'ops'
        ? this.declarations.opsList(this.entityShortId())
        : this.declarations.list(this.entityShortId());
    read$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (resp) => {
        this.rows.set(resp.declarations);
        this.isLoading.set(false);
        this.cdr.markForCheck();
      },
      error: () => {
        this.loadError.set(true);
        this.isLoading.set(false);
        this.cdr.markForCheck();
      },
    });
  }

  /** The live rows of one kind — keyed on `is_in_force`, never on `status`. */
  protected liveOf(kind: DeclarationListKind): Declaration[] {
    return this.rows().filter((r) => r.kind === kind && r.is_in_force && this.inRoles(r));
  }

  /** The add button's words — a card that shows only shareholders adds a shareholder. */
  protected addLabel(spec: { kind: DeclarationListKind; addLabel: string }): string {
    const roles = this.roles();
    return spec.kind === 'DIRECTOR' && roles && !roles.includes('DIRECTOR')
      ? 'Add a shareholder'
      : spec.addLabel;
  }

  private inRoles(row: Declaration): boolean {
    const roles = this.roles();
    return row.kind !== 'DIRECTOR' || !roles || roles.includes(row.director_role ?? '');
  }

  /**
   * The retracted rows of one kind. Shown behind a toggle rather than dropped:
   * the register is append-only, and a list that hides its own corrections
   * cannot be audited.
   */
  protected withdrawnOf(kind: DeclarationListKind): Declaration[] {
    return this.rows().filter(
      (r) => r.kind === kind && !r.is_in_force && r.status === 'DECLARED' && this.inRoles(r),
    );
  }

  protected labelFor(row: Declaration): string {
    if (row.kind === 'FUNDER') return row.funder_name ?? '';
    if (row.kind === 'RELATED_PARTY') return row.party_name ?? '';
    return row.full_name ?? '';
  }

  protected detailFor(row: Declaration): string {
    if (row.kind === 'FUNDER') {
      const type = this.facilityTypes.find((t) => t.value === row.facility_type);
      const state = this.facilityStates.find((s) => s.value === row.facility_state);
      return [type?.label, state?.label].filter(Boolean).join(' · ');
    }
    if (row.kind === 'RELATED_PARTY') {
      return this.relationships.find((r) => r.value === row.relationship)?.label ?? '';
    }
    return this.directorRoles.find((r) => r.value === row.director_role)?.label ?? '';
  }

  // --- Account row rendering -------------------------------------------------

  /** "FNB ····5678" — the tail, never the full number, on a list screen. */
  protected accountLabel(row: Declaration): string {
    const acc = row.bank_account;
    if (!acc) return '—';
    return `${acc.bank_name} ····${acc.account_number_tail}`;
  }

  /**
   * Who holds the account. The `bank_accounts` row's holder is the account's
   * own; `holder_name` is what the CLIENT said about it, and the two are shown
   * separately when they disagree rather than one silently standing in for the
   * other — a third party holding the account is exactly what Ops are reading
   * this list to notice.
   */
  protected accountHolder(row: Declaration): string {
    return row.bank_account?.account_holder_name ?? '';
  }

  protected clientHolderNote(row: Declaration): string {
    const stated = (row.holder_name ?? '').trim();
    if (!stated || stated === this.accountHolder(row)) return '';
    return stated;
  }

  /** The four-state, with the unanswered case as `—` and never as "No". */
  protected tradingLabel(row: Declaration): string {
    return this.tradingStates.find((t) => t.value === row.carries_trading_activity)?.label ?? '—';
  }

  /**
   * `null` means the question was not put — rendered `—`, not "No". Collapsing
   * them would have the screen report a claim the client never made.
   */
  protected principalLabel(row: Declaration): string {
    const value = row.is_principal_trading_account;
    if (value === null || value === undefined) return '—';
    return value ? 'Yes' : 'No';
  }

  /**
   * 🚨 **Rendered on every account row, both ways round.** An account the
   * client declared and an account Ops added from a statement are different
   * acts, and only the first is the client's statement about itself — the
   * distinction FLOW-277's release turns on. Tagging only the Ops rows would
   * leave the client's own declaration as the unlabelled default, which is how
   * the two start being read as one.
   */
  protected sourceLabel(row: Declaration): string {
    // Three acts, three labels: a row CapX added from a statement by hand, one
    // the system READ off a statement's face (FC-DET-047), and the client's own.
    if (row.declared_source === 'OPS_ADDED') return 'Added by CapX';
    if (row.declared_source === 'CIPC') return 'Confirmed by CIPC';
    if (row.declared_source === 'DERIVED_FROM_STATEMENT') return 'Read from a statement';
    return row.corrects ? 'Corrected by the client' : 'Declared by the client';
  }

  /**
   * Ops adding what a statement revealed — the Ops door, so `OPS_ADDED`. The
   * modal builds the payload; {@link save} writes it and re-reads, because
   * adding an account RETRACTS a standing affirmation server-side, so the one
   * POST response is not the whole change.
   */
  protected openAddAccount(): void {
    this.serverError.set(null);
    this.dialog
      .open<AccountDialogComponent, void, DeclarationDialogResult>(AccountDialogComponent, {
        width: '560px',
        autoFocus: 'first-tabbable',
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result) this.save(result);
      });
  }

  /** Open the add form for a list. The dialog builds the payload; we write it. */
  protected openAdd(kind: DeclarationListKind): void {
    this.serverError.set(null);
    this.openDialog({ kind, purpose: 'ADD' });
  }

  /**
   * Correct an entry (N8) — **not** an edit.
   *
   * The register is append-only, so a correction is two acts: the entry is
   * withdrawn, and the corrected one declared. Both stay on record, each
   * attributed to whoever made it. The dialog says so on screen before the
   * person saves; here we only sequence the two writes.
   */
  protected openCorrect(kind: DeclarationListKind, row: Declaration): void {
    this.serverError.set(null);
    this.openDialog({ kind, purpose: 'CORRECT', row });
  }

  private openDialog(data: DeclarationDialogData): void {
    this.dialog
      .open<DeclarationDialogComponent, DeclarationDialogData, DeclarationDialogResult>(
        DeclarationDialogComponent,
        { data, width: '560px', autoFocus: 'first-tabbable' },
      )
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result) return;
        this.save(result);
      });
  }

  /**
   * Write what the dialog built.
   *
   * A correction withdraws first and declares second, and the declare only runs
   * if the withdrawal succeeded — the reverse order would leave the list showing
   * the same fact twice if the second call failed.
   */
  private save(result: DeclarationDialogResult): void {
    this.isSaving.set(true);
    this.serverError.set(null);

    const ops = this.mode() === 'ops';
    const entity = this.entityShortId();
    const declare$ = ops
      ? this.declarations.opsDeclare(entity, result.payload)
      : this.declarations.declare(entity, result.payload);

    const write$ = result.corrects
      ? (ops
          ? this.declarations.opsWithdraw(entity, result.corrects.short_id)
          : this.declarations.withdraw(entity, result.corrects.short_id)
        ).pipe(switchMap(() => declare$))
      : declare$;

    write$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      // Re-read rather than append the returned row: a correction changes
      // `is_in_force` on the withdrawn row too, and a client-side guess at the
      // server's view of "live" is how the two start disagreeing.
      next: () => {
        this.isSaving.set(false);
        this.load();
        this.changed.emit();
      },
      error: (err: unknown) => {
        this.serverError.set(this.messageFrom(err));
        this.isSaving.set(false);
        this.cdr.markForCheck();
      },
    });
  }

  protected toggleWithdrawn(): void {
    this.showWithdrawn.set(!this.showWithdrawn());
  }

  /**
   * Withdraw asks first, in a modal: it takes the entry off the list, and the
   * modal says what that means under N8 — the entry stays on record, marked
   * withdrawn, so nothing is deleted.
   */
  protected confirmWithdraw(row: Declaration): void {
    const name = row.kind === 'ACCOUNT' ? this.accountLabel(row) : this.labelFor(row);
    const data: ConfirmDialogData = {
      title: `Withdraw ${name}?`,
      message:
        'It comes off the list. The entry stays on record, marked as withdrawn, with who ' +
        'withdrew it and when.',
      confirmLabel: 'Withdraw',
    };
    this.dialog
      .open<ConfirmDialogComponent, ConfirmDialogData, boolean>(ConfirmDialogComponent, { data })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        if (confirmed) this.withdraw(row);
      });
  }

  private withdraw(row: Declaration): void {
    this.withdrawingId.set(row.short_id);
    this.serverError.set(null);
    const write$ =
      this.mode() === 'ops'
        ? this.declarations.opsWithdraw(this.entityShortId(), row.short_id)
        : this.declarations.withdraw(this.entityShortId(), row.short_id);
    write$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      // Re-read rather than patch the row in place: the withdrawal changes
      // `is_in_force` on the ORIGINAL row too, and a client-side guess at the
      // server's view of "live" is how the two start disagreeing.
      next: () => {
        this.withdrawingId.set(null);
        this.load();
        this.changed.emit();
      },
      error: (err: unknown) => {
        this.serverError.set(this.messageFrom(err));
        this.withdrawingId.set(null);
        this.cdr.markForCheck();
      },
    });
  }

  private messageFrom(err: unknown): string {
    const detail = (err as { error?: { detail?: unknown } })?.error?.detail;
    if (typeof detail === 'string') return detail;
    return "Couldn't save that. Please try again.";
  }
}
